import type {ReactNode} from 'react';

import type {BusinessCorridorInterest} from '@afriex/core/domain/types';
import {
  getCorridorDirectionLabel,
  getCorridorKey,
  getCountryFlag,
  getCountryName,
  getFeeScheduleChannelLabel,
  sortByCountry,
} from '@afriex/core/utils';

import {Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {cn} from '@/utils/cn';

export type CorridorColumn<Row> = {
  key: string;
  header: string;
  className?: string;
  render: (row: Row) => ReactNode;
};

type CorridorLineTableProps<Row> = {
  rows: Row[];
  getCorridor: (row: Row) => BusinessCorridorInterest;
  columns: CorridorColumn<Row>[];
  /** Name the channel under each corridor; off when a heading already says it. */
  showChannel?: boolean;
  rowClassName?: (row: Row) => string | undefined;
  /** Shown after the country name, such as a status badge. */
  renderCorridorTag?: (row: Row) => ReactNode;
  /** Give the corridor and every column the same width. */
  equalWidths?: boolean;
};

/** A corridor as one cell: flag and country, then currency, direction and channel. */
export const CorridorCell = ({
  corridor,
  showChannel = true,
  tag,
}: {
  corridor: BusinessCorridorInterest;
  showChannel?: boolean;
  tag?: ReactNode;
}) => (
  <>
    <span className="inline-flex flex-wrap items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
      <span>
        {getCountryFlag(corridor.countryCode)}{' '}
        {getCountryName(corridor.countryCode)}
      </span>
      {tag}
    </span>
    <span className="block text-xs text-gray-500 dark:text-gray-400">
      {showChannel
        ? `${corridor.currencyCode} · ${getCorridorDirectionLabel(corridor.type)} · ${getFeeScheduleChannelLabel(corridor.channel)}`
        : corridor.currencyCode}
    </span>
  </>
);

/**
 * One row per corridor, a corridor cell first and the caller's columns after.
 * The negotiation detail and the business fee schedule both read through it,
 * so a corridor is named the same way on every admin screen.
 */
const CorridorLineTable = <Row,>({
  rows,
  getCorridor,
  columns,
  showChannel = true,
  rowClassName,
  renderCorridorTag,
  equalWidths = false,
}: CorridorLineTableProps<Row>) => {
  const width = equalWidths
    ? {width: `${100 / (columns.length + 1)}%`}
    : undefined;
  return (
    <Table>
      <Thead>
        <Tr>
          <Th className="px-4" style={width}>
            Corridor
          </Th>
          {columns.map((column) => (
            <Th
              key={column.key}
              className={cn('px-4', !equalWidths && column.className)}
              style={width}
            >
              {column.header}
            </Th>
          ))}
        </Tr>
      </Thead>
      <TBody>
        {sortByCountry(rows, getCorridor).map((row) => {
          const corridor = getCorridor(row);
          return (
            <Tr key={getCorridorKey(corridor)} className={rowClassName?.(row)}>
              <Td className="px-4">
                <CorridorCell
                  corridor={corridor}
                  showChannel={showChannel}
                  tag={renderCorridorTag?.(row)}
                />
              </Td>
              {columns.map((column) => (
                <Td key={column.key} className="px-4">
                  {column.render(row)}
                </Td>
              ))}
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
};

export default CorridorLineTable;
